/*eslint-disable*/
import React, { useState, useEffect, Suspense } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PostAddIcon from "@mui/icons-material/PostAdd";
import LoginIcon from "@mui/icons-material/Login";
import axios from "axios";

import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import ChatIcon from "@mui/icons-material/Chat";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { logout } from "./api/auth";
import { toast } from "react-toastify";
import { useAuth } from "./context/AuthContext";
import { createListing } from "./api/listings";
// Lazy load PostAdDialog to reduce initial bundle size
const PostAdDialog = React.lazy(() => import("./shared/PostAdDialog"));

// Add environment-aware API base URL
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const Navbar = () => {
  const navigate = useNavigate();
  const { user: authUser, setUser } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const isLoggedIn = !!authUser;
  const userData = authUser ? {
    firstName: authUser.name?.split(" ")[0] || "",
    lastName: authUser.name?.split(" ")[1] || "",
    role: authUser.role,
  } : null;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  // Triggered when user selects the logout option; opens confirmation dialog
  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
    handleClose();
    handleMobileMenuClose();
  };

  // Called when user confirms logout in the dialog
  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setUser(null);
      setLogoutDialogOpen(false);
      navigate("/");
    } catch (error) {
      toast.error("Logout failed");
      setLogoutDialogOpen(false);
    }
  };

  // Called when user cancels logout in the dialog
  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handlePostAdClick = () => {
    if (isLoggedIn) {
      setOpen(true);
      handleMobileMenuClose();
    } else {
      navigate("/login");
      toast.warning("Please login to post an ad");
      handleMobileMenuClose();
    }
  };

  const handleSignInClick = () => {
    navigate("/login");
    handleMobileMenuClose();
  };

  const handleDialogClose = () => {
    setOpen(false);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    handleMobileMenuClose();
  };

  // New handleSubmit function for the PostAdDialog
  const handleSubmit = async (formData) => {
    try {
      setIsSubmitting(true);

      // Create new listing
      await createListing(formData);
      toast.success("Listing created successfully!");
      handleDialogClose();

      // Redirect to manage ads page after successful posting
      navigate("/manage-ads");
    } catch (error) {
      // Handle more specific error messages
      let errorMessage = error.message || "Failed to create listing";

      if (
        errorMessage.includes("Network error") ||
        errorMessage.includes("connection")
      ) {
        errorMessage =
          "Network error. Please check your internet connection and try again.";
      } else if (
        errorMessage.includes("token") ||
        errorMessage.includes("login")
      ) {
        errorMessage = "Please login to continue";
        navigate("/login");
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppBar
      component="nav"
      aria-label="Main navigation"
      position="static"
      sx={{
        backgroundColor: "#f8f8f8",
        color: "#396E74",
        height: { xs: "70px", sm: "100px" },
        minHeight: { xs: "70px", sm: "100px" },
      }}
      elevation={0}
    >
      <Toolbar
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1, sm: 2 },
        }}
      >
        {/* Left side: EXCHANZA */}
        <Typography
          variant="h5"
          component="div"
          sx={{ flexGrow: 1, fontWeight: "bold" }}
        >
          <Link
            to="/"
            style={{
              textDecoration: "none",
              color: "#396E74",
              fontSize: "1.5rem",
            }}
          >
            <img
              src="/assets/logo.png"
              alt="Logo"
              style={{
                height: "40px",
                maxHeight: "40px",
                width: "auto",
              }}
            />
          </Link>
        </Typography>

        {/* Mobile burger menu - only visible on xs */}
        <IconButton
          aria-label="Open menu"
          sx={{
            display: { xs: "block", sm: "none" },
            color: "#396E74",
          }}
          onClick={handleMobileMenuToggle}
        >
          <MenuIcon />
        </IconButton>

        {/* Desktop navigation - hidden on xs */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <Button
            onClick={handlePostAdClick}
            variant="contained"
            sx={{
              backgroundColor: "#396E74",
              borderRadius: "50px",
              height: "50px",
              width: "130px",
              fontSize: "16px",
              fontWeight: 400,
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "#2c545a",
              },
            }}
          >
            Post Ad
          </Button>

          {isLoggedIn ? (
            <IconButton aria-label="User profile menu" onClick={handleClick}>
              {profilePic ? (
                <Avatar src={profilePic} sx={{ width: 60, height: 60 }} />
              ) : (
                <AccountCircleIcon
                  sx={{ fontSize: "2rem", color: "#396E74" }}
                />
              )}
            </IconButton>
          ) : (
            <Button
              component={Link}
              to="/login"
              variant="contained"
              sx={{
                backgroundColor: "#396E74",
                borderRadius: "50px",
                height: "50px",
                width: "130px",
                fontSize: "18px",
                fontWeight: 400,
                "&:hover": {
                  backgroundColor: "#2c545a",
                },
              }}
            >
              Sign In
            </Button>
          )}
        </Box>
      </Toolbar>

      {/* Desktop Menu for user profile */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: "300px",
            mt: 1.5,
            borderRadius: "10px",
            "& .MuiList-root": {
              borderRadius: "10px",
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Avatar sx={{ width: 72, height: 72 }}>
              {userData?.firstName ? userData.firstName[0].toUpperCase() : "U"}
            </Avatar>
            <Box sx={{ ml: 2, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: "#808080" }}>
                Hello,
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {userData
                  ? `${userData.firstName} ${userData.lastName}`
                  : "Loading..."}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        <MenuItem component={Link} to="/manage-ads" sx={{ py: 1.5 }}>
          <ManageAccountsIcon sx={{ mr: 2, color: "#396E74" }} />
          <Typography>Manage Ads</Typography>
        </MenuItem>

        <MenuItem component={Link} to="/favorite" sx={{ py: 1.5 }}>
          <FavoriteIcon sx={{ mr: 2, color: "#396E74" }} />
          <Typography>Favorites and Saved Search</Typography>
        </MenuItem>

        <MenuItem component={Link} to="/public-profile" sx={{ py: 1.5 }}>
          <PersonIcon sx={{ mr: 2, color: "#396E74" }} />
          <Typography>Public Profile</Typography>
        </MenuItem>

        <MenuItem component={Link} to="/chats" sx={{ py: 1.5 }}>
          <ChatIcon sx={{ mr: 2, color: "#396E74" }} />
          <Typography>Chats</Typography>
        </MenuItem>

        <Divider />

        <MenuItem component={Link} to="/settings" sx={{ py: 1.5 }}>
          <SettingsIcon sx={{ mr: 2, color: "#396E74" }} />
          <Typography>Settings</Typography>
        </MenuItem>

        {userData?.role === "admin" && (
          <>
            <Divider />
            <MenuItem component={Link} to="/admin" sx={{ py: 1.5 }}>
              <AdminPanelSettingsIcon sx={{ mr: 2, color: "#396E74" }} />
              <Typography>Admin Panel</Typography>
            </MenuItem>
          </>
        )}

        <Divider />

        <MenuItem sx={{ py: 1.5 }} onClick={handleLogoutClick}>
          <LogoutIcon sx={{ mr: 2, color: "#396E74" }} />
          <Typography>Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        anchor="bottom"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            width: "100%",
            maxHeight: "80vh",
            backgroundColor: "#f8f8f8",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Mobile menu header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#396E74", fontWeight: "bold" }}
            >
              Menu
            </Typography>
            <IconButton
              aria-label="Close menu"
              onClick={handleMobileMenuClose}
              sx={{ color: "#396E74" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* User info section for logged in users */}
          {isLoggedIn && userData && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  backgroundColor: "white",
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                <Avatar sx={{ width: 50, height: 50, mr: 2 }}>
                  {userData.firstName
                    ? userData.firstName[0].toUpperCase()
                    : "U"}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: "#808080" }}>
                    Hello,
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", color: "#396E74" }}
                  >
                    {`${userData.firstName} ${userData.lastName}`}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          {/* Navigation items */}
          <List>
            {/* Post Ad */}
            <ListItem button onClick={handlePostAdClick}>
              <ListItemIcon>
                <PostAddIcon sx={{ color: "#396E74" }} />
              </ListItemIcon>
              <ListItemText primary="Post Ad" sx={{ color: "#396E74" }} />
            </ListItem>

            {/* Sign In or User Menu Items */}
            {!isLoggedIn ? (
              <ListItem button onClick={handleSignInClick}>
                <ListItemIcon>
                  <LoginIcon sx={{ color: "#396E74" }} />
                </ListItemIcon>
                <ListItemText primary="Sign In" sx={{ color: "#396E74" }} />
              </ListItem>
            ) : (
              <>
                <ListItem
                  button
                  onClick={() => handleMenuItemClick("/manage-ads")}
                >
                  <ListItemIcon>
                    <ManageAccountsIcon sx={{ color: "#396E74" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Manage Ads"
                    sx={{ color: "#396E74" }}
                  />
                </ListItem>

                <ListItem
                  button
                  onClick={() => handleMenuItemClick("/favorite")}
                >
                  <ListItemIcon>
                    <FavoriteIcon sx={{ color: "#396E74" }} />
                  </ListItemIcon>
                  <ListItemText primary="Favorites" sx={{ color: "#396E74" }} />
                </ListItem>

                <ListItem button onClick={() => handleMenuItemClick('/public-profile')}>
                  <ListItemIcon>
                    <PersonIcon sx={{ color: "#396E74" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Public Profile"
                    sx={{ color: "#396E74" }}
                  />
                </ListItem>

                <ListItem button onClick={() => handleMenuItemClick('/chats')}>
                  <ListItemIcon>
                    <ChatIcon sx={{ color: "#396E74" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Chats"
                    sx={{ color: "#396E74" }}
                  />
                </ListItem>

                <Divider sx={{ my: 1 }} />

                <ListItem
                  button
                  onClick={() => handleMenuItemClick("/settings")}
                >
                  <ListItemIcon>
                    <SettingsIcon sx={{ color: "#396E74" }} />
                  </ListItemIcon>
                  <ListItemText primary="Settings" sx={{ color: "#396E74" }} />
                </ListItem>

                {userData?.role === "admin" && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <ListItem button onClick={() => handleMenuItemClick("/admin")}>
                      <ListItemIcon>
                        <AdminPanelSettingsIcon sx={{ color: "#396E74" }} />
                      </ListItemIcon>
                      <ListItemText primary="Admin Panel" sx={{ color: "#396E74" }} />
                    </ListItem>
                  </>
                )}

                <Divider sx={{ my: 1 }} />

                <ListItem button onClick={handleLogoutClick}>
                  <ListItemIcon>
                    <LogoutIcon sx={{ color: "#396E74" }} />
                  </ListItemIcon>
                  <ListItemText primary="Logout" sx={{ color: "#396E74" }} />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-confirmation-dialog-title"
      >
        <DialogTitle id="logout-confirmation-dialog-title" sx={{ fontWeight: "bold" }}>
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to logout?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained">
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* PostAdDialog */}
      {open && (
        <Suspense fallback={null}>
          <PostAdDialog
            open={open}
            handleClose={handleDialogClose}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </Suspense>
      )}
    </AppBar>
  );
};

export default Navbar;
